import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox"; // Using shadcn Checkbox
import { Label } from "@/components/ui/label"; // Using shadcn Label
import { useAuth } from "../../context/AuthContext"; // Relative path
import { Loader2 } from "lucide-react"; // Import Loader2
import { useForm, Controller } from "react-hook-form"; // Import Controller
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { toast } from "sonner";
// No direct navigation here, AuthContext handles it
// import { useNavigate } from "react-router-dom";
import { UserRole } from "../../lib/types/userTypes"; // Relative path
import { RegisterRequest } from "../../lib/types/authTypes"; // Relative path

const formSchema = z
  .object({
    name: z.string().min(1, "Full name is required"),
    email: z.string().email("Invalid email address"),
    password: z.string().min(8, "Password must be at least 8 characters long"),
    confirmPassword: z.string(),
    role: z
      .enum(["user", "agent", "admin", "superadmin"] as [
        UserRole,
        ...UserRole[]
      ])
      .optional(), // Match backend UserRole
    termsAndPrivacyAccepted: z.boolean().refine((val) => val === true, {
      message: "You must accept the Terms of Service and Privacy Policy",
    }),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"], // Point error to confirmPassword field
  });

type RegisterFormValues = z.infer<typeof formSchema>;

const RegisterForm = () => {
  const { register: registerUser, isLoading } = useAuth(); // Renamed to avoid conflict
  // const navigate = useNavigate(); // Navigation is handled by AuthContext or page

  const form = useForm<RegisterFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      email: "",
      password: "",
      confirmPassword: "",
      role: "user", // Default role
      termsAndPrivacyAccepted: false,
    },
  });

  const onSubmit = async (values: RegisterFormValues) => {
    try {
      // Prepare data for the API, role might be optional or handled by backend if not provided
      const apiData: RegisterRequest = {
        name: values.name,
        email: values.email,
        password: values.password,
        termsAndPrivacyAccepted: values.termsAndPrivacyAccepted,
      };
      if (values.role) {
        apiData.role = values.role;
      }

      await registerUser(apiData);
      // Navigation is handled by AuthContext based on API response (e.g., to /verify-email)
      // form.reset(); // Optionally reset form on success
    } catch (error) {
      // Error toast is handled by AuthContext
      console.error("Registration form error:", error);
    }
  };

  return (
    <form className="space-y-6" onSubmit={form.handleSubmit(onSubmit)}>
      <div>
        <Label htmlFor="name">Full name</Label>
        <Input
          id="name"
          type="text"
          {...form.register("name")}
          className="w-full mt-1"
          disabled={isLoading}
        />
        {form.formState.errors.name && (
          <p className="mt-1 text-xs text-red-600">
            {form.formState.errors.name.message}
          </p>
        )}
      </div>

      <div>
        <Label htmlFor="email">Email address</Label>
        <Input
          id="email"
          type="email"
          autoComplete="email"
          {...form.register("email")}
          className="w-full mt-1"
          disabled={isLoading}
        />
        {form.formState.errors.email && (
          <p className="mt-1 text-xs text-red-600">
            {form.formState.errors.email.message}
          </p>
        )}
      </div>

      <div>
        <Label htmlFor="password">Password</Label>
        <Input
          id="password"
          type="password"
          {...form.register("password")}
          className="w-full mt-1"
          disabled={isLoading}
        />
        {form.formState.errors.password ? (
          <p className="mt-1 text-xs text-red-600">
            {form.formState.errors.password.message}
          </p>
        ) : (
          <p className="mt-1 text-xs text-gray-500">
            Use at least 8 characters.
          </p>
        )}
      </div>

      <div>
        <Label htmlFor="confirmPassword">Confirm Password</Label>
        <Input
          id="confirmPassword"
          type="password"
          {...form.register("confirmPassword")}
          className="w-full mt-1"
          disabled={isLoading}
        />
        {form.formState.errors.confirmPassword && (
          <p className="mt-1 text-xs text-red-600">
            {form.formState.errors.confirmPassword.message}
          </p>
        )}
      </div>

      {/* Optional Role Picker - can be added if needed, for now defaults to 'user' */}
      {/* <div>
        <Label htmlFor="role">Role</Label>
        <select {...form.register("role")} id="role" className="w-full mt-1" disabled={isLoading}>
          <option value="user">User</option>
          <option value="agent">Agent</option>
        </select>
      </div> */}

      <div className="flex items-start space-x-2">
        <Controller
          name="termsAndPrivacyAccepted"
          control={form.control}
          render={({ field }) => (
            <Checkbox
              id="termsAndPrivacyAccepted"
              checked={field.value}
              onCheckedChange={field.onChange}
              disabled={isLoading}
            />
          )}
        />
        <div className="grid gap-1.5 leading-none">
          <label
            htmlFor="termsAndPrivacyAccepted" // This still needs to point to the Checkbox's ID
            className="text-sm text-gray-700 font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
          >
            I agree to the{" "}
            <a
              href="/terms-of-service"
              target="_blank"
              rel="noopener noreferrer"
              className="text-propertylk-blue hover:text-propertylk-darkblue"
            >
              Terms of Service
            </a>{" "}
            and{" "}
            <a
              href="/privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-propertylk-blue hover:text-propertylk-darkblue"
            >
              Privacy Policy
            </a>
          </label>
          {form.formState.errors.termsAndPrivacyAccepted && (
            <p className="text-xs text-red-600">
              {form.formState.errors.termsAndPrivacyAccepted.message}
            </p>
          )}
        </div>
      </div>

      <div>
        <Button
          type="submit"
          className="w-full bg-propertylk-blue hover:bg-propertylk-darkblue flex items-center justify-center"
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Creating account...
            </>
          ) : (
            "Create account"
          )}
        </Button>
      </div>
    </form>
  );
};

export default RegisterForm;
